import type { AutomationArtifactSummary } from "@repo/app-types/entities/automationArtifact";
import { asPlaybookId } from "@repo/app-types/entities/playbook";
import { formatRunTime } from "@repo/ui/automations/automationPresentation";
import { resolveArtifactSentAt } from "@repo/ui/automations/automationTables";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeaderFilterBarPortal } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import type { FC } from "react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useMyAutomationResultsInfiniteQuery } from "../../api/entities/automationArtifact.js";
import { PageBody, TableCard } from "../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";

/**
 * The teammate-facing "Notifications" list: automation results delivered to the
 * current teammate. Deliberately narrower than the editor Results tab — it
 * shows only the result and when it arrived, never the other recipients or
 * managers a report went to.
 */
export const TrainingTeammatePageAutomationResults: FC = () => {
  const navigate = useNavigate();
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId route param.");
  }
  const playbookId = asPlaybookId(playbookIdParam);
  const { data, isLoading, isError, hasNextPage, fetchNextPage } =
    useMyAutomationResultsInfiniteQuery({ playbookId });
  const artifacts = useMemo(
    () => data?.pages.flatMap((page) => page.artifacts),
    [data],
  );

  const columns = useMemo<
    readonly EntityTableColumnDefinition<AutomationArtifactSummary>[]
  >(
    () => [
      {
        id: "title",
        header: "Notification",
        className: "w-[90%]",
        isRowHeader: true,
        getTextValue: (artifact) => artifact.title,
        renderCell: (artifact) => (
          <span className="font-medium text-foreground">{artifact.title}</span>
        ),
      },
      {
        id: "received",
        header: "Received",
        className: "w-[10%]",
        getTextValue: (artifact) => {
          const sentAt = resolveArtifactSentAt(artifact);
          return sentAt ? formatRunTime(sentAt) : "—";
        },
        renderCell: (artifact) => {
          const sentAt = resolveArtifactSentAt(artifact);
          return (
            <span className="text-muted-foreground">
              {sentAt ? formatRunTime(sentAt) : "—"}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <PageHeaderFilterBarPortal>
        <FilterBar
          leadingItems={<PerformancePlaybookSwitcher staysOnFullPath />}
        />
      </PageHeaderFilterBarPortal>
      <PageBody>
        <TableCard>
          <EntityTable
            aria-label="Notifications"
            columns={columns}
            entities={artifacts}
            isLoading={isLoading}
            isError={isError}
            isFullBleed
            onLoadMore={hasNextPage ? fetchNextPage : undefined}
            emptyMessage="No notifications yet. Results sent to you by automations appear here."
            onEntityAction={(artifact) => {
              navigate(encodeURIComponent(artifact.id));
            }}
            size={Size.SMALL}
          />
        </TableCard>
      </PageBody>
    </>
  );
};
